import * as ft from 'funtypes'
import { IsoDateTime, Latitude, Longitude, nullable, objectWith } from '../validation.js'

/**
 * Runtypes mirroring the EIPA v2.0 JSON files published by UDT.
 *
 * Source of truth: https://eipa.udt.gov.pl/reader/docs (schema, optionality)
 * and the normative identifier tables in the UDT system description
 * (http://bip.udt.gov.pl/images/EIPA-opis-systemu.pdf, Tabela 1-3).
 *
 * Hierarchy: operator -> pool (a physical site, carries lat/lon and address)
 * -> station -> point -> connectors.
 *
 * These types describe the wire format and nothing else. Do not add derived or
 * convenience fields here - that is what the canonical domain model in
 * `../domain/types.ts` is for.
 */

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

/** Every EIPA file wraps its payload in `{ data, generated }`. */
export const EipaEnvelope = <T extends ft.RuntypeBase>(data: T) =>
  ft.Object({
    data,
    /** When UDT generated this file. */
    generated: IsoDateTime,
  })

/** The row-oriented flavour of {@link EipaEnvelope}: `data` is an array. */
export const EipaRowsEnvelope = <T extends ft.RuntypeBase>(row: T) => EipaEnvelope(ft.Array(row))

/** Static counterpart of {@link EipaEnvelope}, for generic helper signatures. */
export interface EipaEnvelopeOf<T> {
  readonly data: T
  readonly generated: string
}

// ---------------------------------------------------------------------------
// dictionary.json
// ---------------------------------------------------------------------------

/** Dictionary row that carries a machine-readable name, e.g. `Mode3-AC-3p`. */
export const EipaNamedEntry = ft.Object({ id: ft.Number, name: ft.String })
export type EipaNamedEntry = ft.Static<typeof EipaNamedEntry>

/** Dictionary row with a machine name plus a human description. */
export const EipaDescribedEntry = objectWith(
  { id: ft.Number, name: ft.String },
  { description: nullable(ft.String) },
)
export type EipaDescribedEntry = ft.Static<typeof EipaDescribedEntry>

/**
 * Dictionary row that only carries a Polish description. Used by the
 * authentication and payment tables, whose ids are the normative bit flags of
 * Tabela 1 / Tabela 2 rather than sequential keys.
 */
export const EipaDescriptionOnlyEntry = ft.Object({ id: ft.Number, description: ft.String })
export type EipaDescriptionOnlyEntry = ft.Static<typeof EipaDescriptionOnlyEntry>

/**
 * A country. Unlike every other dictionary table, `id` is the lowercase
 * ISO 3166-1 alpha-2 code (`"pl"`), not a number - it is what `operator.country`
 * references. There is no separate `code` field.
 */
export const EipaCountryEntry = ft.Object({ id: ft.String, name: ft.String })
export type EipaCountryEntry = ft.Static<typeof EipaCountryEntry>

/** `dictionary.json` payload: a single object keyed by dictionary name. */
export const EipaDictionary = ft.Object({
  /** IEC 61851 charging modes, e.g. `Mode3-AC-3p`. */
  charging_mode: ft.Array(EipaNamedEntry),
  /** Electric connector types (Tabela 3), e.g. `IEC-62196-T2-F-NOCABLE`. */
  connector_interface: ft.Array(EipaDescribedEntry),
  /** LNG fuel standards, e.g. `zimne`, `saturowane`. */
  fuel_type: ft.Array(EipaDescribedEntry),
  /** CNG/LNG connector types, e.g. `NGV1`. */
  gas_connector_interface: ft.Array(EipaDescribedEntry),
  /** Hydrogen refuelling solutions, e.g. `700 bar - osobowe`. */
  hydrogen_refill_solution: ft.Array(EipaDescribedEntry),
  /** Tabela 1. Ids are bit flags, not sequential. */
  station_authentication_method: ft.Array(EipaDescriptionOnlyEntry),
  /** Tabela 2. Ids are bit flags, not sequential. */
  station_payment_method: ft.Array(EipaDescriptionOnlyEntry),
  /** 1 operator, 2 service provider, 3 both. */
  company_type: ft.Array(EipaNamedEntry),
  country: ft.Array(EipaCountryEntry),
  /** 1 = Monday ... 7 = Sunday. */
  weekday: ft.Array(EipaNamedEntry),
})
export type EipaDictionary = ft.Static<typeof EipaDictionary>

/**
 * `dictionary` is the one file UDT publishes *without* the `{data, generated}`
 * envelope - the response body is the bare table object. It is normalised into
 * the same envelope as its five siblings by {@link toEipaDictionaryEnvelope} so
 * the rest of the pipeline can treat the six alike, which is why `generated` is
 * optional here and required everywhere else.
 */
export const EipaDictionaryFile = objectWith({ data: EipaDictionary }, { generated: IsoDateTime })
export type EipaDictionaryFile = ft.Static<typeof EipaDictionaryFile>

/**
 * Wraps the bare wire payload in the envelope the pipeline expects.
 *
 * Accepts an already-enveloped object unchanged, so a fixture written against
 * the assumed shape and a body straight off the wire both parse. Applied by the
 * client on download and by the archive reader on replay - the archived bytes
 * are UDT's own, envelope-less.
 */
export const toEipaDictionaryEnvelope = (payload: unknown): unknown =>
  typeof payload === 'object' && payload !== null && 'data' in payload ? payload : { data: payload }

// ---------------------------------------------------------------------------
// operator.json
// ---------------------------------------------------------------------------

/** 1 operator, 2 service provider, 3 both. */
export const EipaOperatorType = ft.Union(ft.Literal(1), ft.Literal(2), ft.Literal(3))
export type EipaOperatorType = ft.Static<typeof EipaOperatorType>

export const EipaOperator = objectWith(
  {
    id: ft.Number,
    code: ft.String,
    name: ft.String,
    phone: ft.String,
    type: EipaOperatorType,
    /** Lowercase ISO 3166-1 alpha-2, keyed into `dictionary.country`. */
    country: ft.String,
  },
  {
    /** Absent on roughly a quarter of operators. */
    short_name: nullable(ft.String),
    email: nullable(ft.String),
    website: nullable(ft.String),
  },
)
export type EipaOperator = ft.Static<typeof EipaOperator>

export const EipaOperatorFile = EipaRowsEnvelope(EipaOperator)
export type EipaOperatorFile = ft.Static<typeof EipaOperatorFile>

// ---------------------------------------------------------------------------
// pool.json - the physical site
// ---------------------------------------------------------------------------

export const EipaImage = ft.Object({
  uri: ft.String,
  thumbnail_uri: ft.String,
  ts: IsoDateTime,
})
export type EipaImage = ft.Static<typeof EipaImage>

/** A window during which the pool is shut, e.g. a renovation. */
export const EipaClosingHours = ft.Object({
  from_time: IsoDateTime,
  to_time: IsoDateTime,
})
export type EipaClosingHours = ft.Static<typeof EipaClosingHours>

/** A weekly opening window. `weekday` references `dictionary.weekday`. */
export const EipaOperatingHours = ft.Object({
  from_time: ft.String,
  to_time: ft.String,
  weekday: ft.Number,
})
export type EipaOperatingHours = ft.Static<typeof EipaOperatingHours>

export const EipaPool = objectWith(
  {
    id: ft.Number,
    code: ft.String,
    operator_id: ft.Number,
    name: ft.String,
    /** Whether the pool offers electric charging stations. */
    charging: ft.Boolean,
    /** Whether the pool offers gas/hydrogen filling stations. */
    filling: ft.Boolean,
    street: ft.String,
    house_number: ft.String,
    postal_code: ft.String,
    city: ft.String,
    latitude: Latitude,
    longitude: Longitude,
    ts: IsoDateTime,
  },
  {
    /** Free-text access directions. Absent on about three quarters of pools. */
    accessibility: nullable(ft.String),
    /** Metres above sea level. Absent far more often than it is present. */
    elevation: nullable(ft.Number),
    closing_hours: nullable(ft.Array(EipaClosingHours)),
    house_number_addition: nullable(ft.String),
    images: nullable(ft.Array(EipaImage)),
    /** Pool-level overrides of the operator's own contact details. */
    operator_name: nullable(ft.String),
    operator_phone: nullable(ft.String),
    operator_website: nullable(ft.String),
    operator_email: nullable(ft.String),
    operating_hours: nullable(ft.Array(EipaOperatingHours)),
    /** GUS TERYT code of the commune. Present on a handful of pools. */
    teryt: nullable(ft.String),
    /** Whether the pool offers CNG/LNG filling, and hydrogen, respectively. */
    refilling: ft.Boolean,
    h2refilling: ft.Boolean,
    /** Whether the meters are legalised for billing. */
    legalized: ft.Boolean,
    /**
     * `energy`, `gas`, `cng`, `lng`, `h2`.
     *
     * Normally an array. A couple of rows arrive as an object keyed by stringified
     * index (`{"0":"gas","1":"cng","3":"lng"}`) - a PHP sparse array that did not
     * survive JSON encoding - so both forms are accepted rather than losing the row.
     */
    features: ft.Union(ft.Array(ft.String), ft.Record(ft.String, ft.String)),
  },
)
export type EipaPool = ft.Static<typeof EipaPool>

export const EipaPoolFile = EipaRowsEnvelope(EipaPool)
export type EipaPoolFile = ft.Static<typeof EipaPoolFile>

// ---------------------------------------------------------------------------
// station.json
// ---------------------------------------------------------------------------

/** E electric, G gas (CNG/LNG), H hydrogen. */
export const EipaStationType = ft.Union(ft.Literal('E'), ft.Literal('G'), ft.Literal('H'))
export type EipaStationType = ft.Static<typeof EipaStationType>

/**
 * Administrative location. Every field is optional: a few stations carry an
 * empty `{}` here rather than omitting the key or sending null.
 */
export const EipaStationLocation = ft.Partial({
  city: ft.String,
  community: ft.String,
  district: ft.String,
  province: ft.String,
})
export type EipaStationLocation = ft.Static<typeof EipaStationLocation>

export const EipaStation = objectWith(
  {
    id: ft.Number,
    pool_id: ft.Number,
    type: EipaStationType,
    ts: IsoDateTime,
  },
  {
    /** Absent on many rows; fall back to the parent pool's coordinates. */
    latitude: nullable(Latitude),
    longitude: nullable(Longitude),
    /** Bit-flag ids from `dictionary.station_authentication_method`. */
    authentication_methods: nullable(ft.Array(ft.Number)),
    /** Bit-flag ids from `dictionary.station_payment_method`. */
    payment_methods: nullable(ft.Array(ft.Number)),
    images: nullable(ft.Array(EipaImage)),
    location: nullable(EipaStationLocation),
  },
)
export type EipaStation = ft.Static<typeof EipaStation>

export const EipaStationFile = EipaRowsEnvelope(EipaStation)
export type EipaStationFile = ft.Static<typeof EipaStationFile>

// ---------------------------------------------------------------------------
// point.json
// ---------------------------------------------------------------------------

/** `mode` references `dictionary.charging_mode`; `power` is kW. */
export const EipaChargingSolution = ft.Object({
  mode: ft.Number,
  power: ft.Number,
})
export type EipaChargingSolution = ft.Static<typeof EipaChargingSolution>

export const EipaConnector = ft.Object({
  /** Ids from `dictionary.connector_interface`. Resolve at runtime, never hardcode. */
  interfaces: ft.Array(ft.Number),
  cable_attached: ft.Boolean,
  /** Maximum kW obtainable through this connector. */
  power: ft.Number,
  ts: IsoDateTime,
})
export type EipaConnector = ft.Static<typeof EipaConnector>

/**
 * A charging or filling point.
 *
 * EIPA publishes one row shape for all three station types: the arrays that do
 * not apply are present but empty (an electric point carries an empty
 * `gas_connector_interfaces`, a hydrogen point an empty `connectors`). Only
 * `gas_type` and `max_output` are genuinely absent outside gas/hydrogen rows.
 */
export const EipaPoint = objectWith(
  {
    id: ft.Number,
    code: ft.String,
    station_id: ft.Number,
    ts: IsoDateTime,
    /** Electric: IEC 61851 mode + kW pairs. Empty for gas and hydrogen. */
    charging_solutions: ft.Array(EipaChargingSolution),
    /** Electric: physical connectors. Empty for gas and hydrogen. */
    connectors: ft.Array(EipaConnector),
    /** LNG only: ids from `dictionary.fuel_type`. Empty otherwise. */
    fuel_types: ft.Array(ft.Number),
    /** Gas only: ids from `dictionary.gas_connector_interface`. Empty otherwise. */
    gas_connector_interfaces: ft.Array(ft.Number),
    /** Hydrogen only: ids from `dictionary.hydrogen_refill_solution`. Empty otherwise. */
    hydrogen_refill_solutions: ft.Array(ft.Number),
  },
  {
    /** `CNG` or `LNG` on gas points; empty string on hydrogen points. */
    gas_type: nullable(ft.String),
    /** Nm3/hr for CNG, L/min for LNG. Empty string on hydrogen points. */
    max_output: nullable(ft.Union(ft.Number, ft.String)),
    /** The unit {@link EipaPoint.max_output} is quoted in: `Nm3/h` or `l/min`. */
    max_output_unit: nullable(ft.String),
  },
)
export type EipaPoint = ft.Static<typeof EipaPoint>

export const EipaPointFile = EipaRowsEnvelope(EipaPoint)
export type EipaPointFile = ft.Static<typeof EipaPointFile>

// ---------------------------------------------------------------------------
// dynamic.json
// ---------------------------------------------------------------------------

/** Known values of `EipaPrice.unit`. Not exhaustive - operators do invent more. */
export const EIPA_PRICE_UNITS = {
  kwh: 'kWh',
  minute: 'min',
  cubicMetre: 'm3',
  kilogram: 'kg',
} as const

export const EipaPrice = objectWith(
  { ts: IsoDateTime },
  {
    /**
     * PLN per {@link EipaPrice.unit}, as a decimal **string** - `"1.00"`, not
     * `1`. UDT serialises the tariff as a fixed-point string, so this is the
     * wire type; {@link parseEipaPrice} is what turns it into a number.
     *
     * Absent on a small number of rows, which carry only a `literal` tariff.
     */
    price: nullable(ft.String),
    /** `kWh`, `min`, `m3` or `kg`. See {@link EIPA_PRICE_UNITS}. Occasionally absent. */
    unit: nullable(ft.String),
    /** Free-text tariff or the operator's own tariff id, e.g. `PL*EVP*EPL105*105A`. */
    literal: nullable(ft.String),
  },
)
export type EipaPrice = ft.Static<typeof EipaPrice>

/**
 * The numeric value of a wire price, or `null` when it is absent or not a
 * number.
 *
 * `Number('')` is 0 and `Number(null)` is 0, either of which would publish a
 * free tariff for a point that never quoted one, so both are rejected here
 * rather than coerced.
 */
export const parseEipaPrice = (price: string | null | undefined): number | null => {
  if (price === null || price === undefined || price.trim() === '') return null
  const value = Number(price)
  return Number.isFinite(value) ? value : null
}

/** 1 available, 0 not available. */
export const EipaAvailability = ft.Union(ft.Literal(0), ft.Literal(1))
export type EipaAvailability = ft.Static<typeof EipaAvailability>

/** 1 free, 0 occupied. */
export const EipaOccupancy = ft.Union(ft.Literal(0), ft.Literal(1))
export type EipaOccupancy = ft.Static<typeof EipaOccupancy>

export const EipaStatus = ft.Object({
  availability: EipaAvailability,
  status: EipaOccupancy,
  ts: IsoDateTime,
})
export type EipaStatus = ft.Static<typeof EipaStatus>

export const EipaDynamic = objectWith(
  { point_id: ft.Number },
  {
    /** The point's EIPA code, repeated from `point.code`. */
    code: nullable(ft.String),
    prices: nullable(ft.Array(EipaPrice)),
    /** Electric points only; gas and hydrogen rows carry prices but no status. */
    status: nullable(EipaStatus),
    /** Gas and hydrogen rows only: `Nm3/h` or `l/min`. */
    max_output_unit: nullable(ft.String),
  },
)
export type EipaDynamic = ft.Static<typeof EipaDynamic>

export const EipaDynamicFile = EipaRowsEnvelope(EipaDynamic)
export type EipaDynamicFile = ft.Static<typeof EipaDynamicFile>

// ---------------------------------------------------------------------------
// The six files together
// ---------------------------------------------------------------------------

/**
 * Archive and display name per file.
 *
 * These are *not* the URL path segments - a reader URL is
 * `<base>/<key>/<token>`, with no `.json` suffix (see {@link EIPA_FILE_CLASS}
 * and the client's `urlFor`). They name the object in `raw/<date>/` and the
 * file in an error message, both of which predate the real URLs and are stable.
 */
export const EIPA_FILES = {
  dictionary: 'dictionary.json',
  operator: 'operator.json',
  pool: 'pool.json',
  station: 'station.json',
  point: 'point.json',
  dynamic: 'dynamic.json',
} as const

export type EipaFileName = keyof typeof EIPA_FILES

/**
 * Which of UDT's two hourly download limits a file draws on.
 *
 * The limits are published per *class*, not per account and not per file: the
 * five static files share one allowance and `dynamic` has its own, far larger
 * one. Everything about the request budget derives from this split.
 */
export const EIPA_FILE_CLASS = {
  dictionary: 'static',
  operator: 'static',
  pool: 'static',
  station: 'static',
  point: 'static',
  dynamic: 'dynamic',
} as const satisfies Record<EipaFileName, 'static' | 'dynamic'>

export type EipaFileClass = (typeof EIPA_FILE_CLASS)[EipaFileName]

/** The five files behind the tighter of the two hourly limits. */
export const EIPA_STATIC_FILES = (Object.keys(EIPA_FILE_CLASS) as EipaFileName[]).filter(
  (file) => EIPA_FILE_CLASS[file] === 'static',
)

/** Every EIPA file, parsed. The shape {@link EipaClient.fetchAll} returns. */
export interface EipaSnapshot {
  readonly dictionary: EipaDictionaryFile
  readonly operator: EipaOperatorFile
  readonly pool: EipaPoolFile
  readonly station: EipaStationFile
  readonly point: EipaPointFile
  readonly dynamic: EipaDynamicFile
}
